import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@copro/api";
import { _3_DAYS } from "../../../constants/durations";
import type { ActivityTimelineFeed } from "../../../types";
import { getTimelineScope, isTimelineEnabled, type TimelineFilters } from "./useGetActivityTimeline";
import { keys } from "./keys";

// Shared by every preview so they hit the same cached query.
export const RECENT_ACTIVITIES_LIMIT = 2;

async function getRecentActivities(
  filters: TimelineFilters,
  limit: number
): Promise<{ items: ActivityTimelineFeed[]; total: number }> {
  let query = apiClient
    .from('activity_timeline_feed')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false, nullsFirst: false })
    .order('id', { ascending: false })
    .limit(limit);

  for (const [column, value] of getTimelineScope(filters)) {
    query = query.eq(column, value);
  }

  const { data, error, count } = await query;

  if (error) {
    throw error;
  }

  return { items: data ?? [], total: count ?? 0 };
}

/** The `limit` latest activities plus the total count, for previews that link to the full journal. */
export function useGetRecentActivities(filters: TimelineFilters, limit: number) {
  return useQuery({
    queryKey: keys.activityTimelineRecent(filters, limit),
    queryFn: () => getRecentActivities(filters, limit),
    enabled: isTimelineEnabled(filters),
    staleTime: _3_DAYS,
  });
}
